import { forwardRef, useState, useEffect, useRef, useCallback } from "react";
import {
  TextInput as MantineTextInput,
  TextInputProps as MantineTextInputProps,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import classes from "@/styles/TextInput.module.css";
import { ActionIcon } from "./ActionIcon";
import { useStyles } from "@/styles/useStyles";

type MantineTextInputValue = string;

export interface TextInputProps
  extends Omit<MantineTextInputProps, "value" | "title"> {
  id: string;
  value: MantineTextInputValue;
  defaultValue?: MantineTextInputValue;
  onDebouncedChange?: (value: MantineTextInputValue) => void;
  debounceDelay?: number;
  onClear?: () => void;
  multiline?: boolean;
  title: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      id,
      onDebouncedChange,
      debounceDelay = 300,
      onClear,
      value,
      title,
      ...props
    },
    _ref,
  ) => {
    const { theme } = useStyles();
    const [localValue, setLocalValue] = useState<MantineTextInputValue>(
      value || props.defaultValue || "",
    );

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.currentTarget.value;
        setLocalValue(newValue);
        props.onChange?.(event);

        // Clear existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Set new timeout
        timeoutRef.current = setTimeout(() => {
          onDebouncedChange?.(newValue);
        }, debounceDelay);
      },
      [props, debounceDelay, onDebouncedChange],
    );

    const handleClear = useCallback(() => {
      setLocalValue("");
      onClear?.();

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }, [onClear]);

    return (
      <MantineTextInput
        id={id}
        name={id}
        value={localValue}
        title={title}
        aria-label={title}
        aria-invalid={props.error ? true : undefined}
        aria-errormessage={props.error ? `${id}-error` : undefined}
        onChange={handleChange as any}
        classNames={{ input: classes.textInput }}
        rightSection={
          value && onClear ? (
            <ActionIcon
              ariaLabel="Clear Advizer Search"
              onClick={handleClear}
              variant="transparent"
              color={!value ? "text.4" : "text.6"}
            >
              <IconX />
            </ActionIcon>
          ) : null
        }
        styles={{
          label: {
            color: theme.colors.text[9],
            fontWeight: 700,
          },
          // ...props.styles,
        }}
        {...props}
      />
    );
  },
);

TextInput.displayName = "TextInput";
